import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams, type NavigateOptions } from 'react-router-dom';
import { MessagesSquare } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useLiveOverview } from '../contexts/LiveOverviewContext';
import { useDebounce } from '../hooks/useDebounce';
import authService, { type AgentNumberConfig } from '../services/authService';
import conversationService, { type ConversationDetailResponse } from '../services/conversationService';
import callbackTicketService, { type CallbackTicket } from '../services/callbackTicketService';
import { ApiError } from '../services/apiService';
import {
  buildListSections,
  chipChannel,
  findLiveCall,
  pickBestMatch,
  selectionForItem,
  selectionForRow,
  waitingCount,
  type ListChip,
  type ListRow,
} from '@/lib/conversationList';
import {
  parseChip,
  parseJump,
  parseSelection,
  sameSelection,
  wantsComposerFocus,
  withListFilters,
  withSelection,
  withoutComposerFocus,
  withoutJump,
  type Selection,
} from '@/lib/conversationUrl';
import { displayName, formatPhone, phoneDigits } from '@/lib/conversationFormat';
import ConversationListPane from '../components/conversations/ConversationListPane';
import ContactWorkspace from '../components/conversations/ContactWorkspace';
import CallWorkspace from '../components/conversations/CallWorkspace';
import SessionWorkspace from '../components/conversations/SessionWorkspace';
import MobileChatDialog from '../components/conversations/MobileChatDialog';
import type { AgentLabel } from '../components/conversations/ContextPanel';
import { ChatSkeleton, type WorkspaceChrome } from '../components/conversations/WorkspaceFrame';
import { useConversationList } from '../components/conversations/useConversationList';
import { useServerNow } from '../components/conversations/useServerNow';
import { usePageTitle } from '../lib/documentTitle';

const DESKTOP_QUERY = '(min-width: 768px)';
const WIDE_QUERY = '(min-width: 1280px)';
const CONTEXT_OPEN_KEY = 'verlaeufe:context-open';

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    setMatches(mq.matches);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

/** Per-browser convenience: whether the context column is shown on wide screens. */
function readContextOpen(): boolean {
  try {
    return window.localStorage.getItem(CONTEXT_OPEN_KEY) !== '0';
  } catch {
    return true;
  }
}

function writeContextOpen(open: boolean) {
  try {
    window.localStorage.setItem(CONTEXT_OPEN_KEY, open ? '1' : '0');
  } catch {
    // Storage blocked (private mode): the toggle still works for this visit.
  }
}

interface ListNavState {
  /** The chat was opened from the list: "back" returns there via history. */
  fromList?: boolean;
}

/**
 * Verläufe — the salon's live center: the list of contacts and calls (what
 * needs the salon pinned on top), the continuous WhatsApp chat per contact
 * with the handoff status, live phone calls, and the context of the open one.
 */
export default function ConversationsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const isWide = useMediaQuery(WIDE_QUERY);
  const { currentUser } = useAuth();
  const me = currentUser?.email ?? null;
  const { overview, applyCallbackTicket, refresh: refreshOverview } = useLiveOverview();
  const now = useServerNow(30_000);

  // The URL is the source of truth for what is open; this ref always holds
  // the newest params, also between a navigate() and the render that shows
  // it. It only follows the router when the router's params really changed:
  // a render in between (e.g. from a state update) still carries the OLD
  // params, and adopting them would undo our own navigation.
  const routerSearch = searchParams.toString();
  const paramsRef = useRef(searchParams);
  const seenRouterSearchRef = useRef(routerSearch);
  if (seenRouterSearchRef.current !== routerSearch) {
    seenRouterSearchRef.current = routerSearch;
    paramsRef.current = searchParams;
  }
  // A replace keeps the entry's history state (`fromList`), or "back" on the
  // phone would no longer know that it may simply pop to the list.
  const locationStateRef = useRef(location.state);
  locationStateRef.current = location.state;
  const updateParams = useCallback(
    (fn: (params: URLSearchParams) => URLSearchParams, opts: NavigateOptions = {}) => {
      const next = fn(new URLSearchParams(paramsRef.current));
      if (next.toString() === paramsRef.current.toString() && !opts.state) return;
      paramsRef.current = next;
      const search = next.toString();
      const state = opts.state === undefined && opts.replace ? locationStateRef.current : opts.state;
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : '' },
        { preventScrollReset: true, ...opts, state },
      );
    },
    [navigate, location.pathname],
  );

  // Async work (a jump lookup) must not navigate once the page is gone.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const selection = parseSelection(searchParams);
  const chip = parseChip(searchParams);
  const [search, setSearch] = useState(() => searchParams.get('q') ?? searchParams.get('phone') ?? '');
  const query = useDebounce(search, 300);

  // Tab title instead of a page heading (the bell adds its "(n) " prefix).
  usePageTitle('Verläufe · TimeGlobe');

  // --- agents ---------------------------------------------------------------------
  const [agents, setAgents] = useState<AgentNumberConfig[]>([]);
  const [agentFilter, setAgentFilter] = useState('');
  useEffect(() => {
    authService
      .getNumberConfigs()
      .then((res) => setAgents(res.numbers || []))
      .catch(() => setAgents([]));
  }, []);
  const agentLabel = useCallback<AgentLabel>(
    (agentNumber) => {
      if (!agentNumber) return null;
      const digits = phoneDigits(agentNumber);
      const agent = agents.find((a) => phoneDigits(a.phone_number) === digits);
      return agent?.display_name?.trim() || formatPhone(agentNumber);
    },
    [agents],
  );

  // --- list -----------------------------------------------------------------------
  // A `?phone=` jump rewrites chip + search first; loading the list for the
  // filters it is about to replace (incl. the debounced search catching up
  // with the jump's number) would be wasted requests.
  const [jumpQuery, setJumpQuery] = useState<string | null>(null);
  useEffect(() => {
    if (jumpQuery !== null && query === jumpQuery) setJumpQuery(null);
  }, [query, jumpQuery]);
  const jumpPending = parseJump(searchParams) !== null || (jumpQuery !== null && query !== jumpQuery);
  const list = useConversationList({ channel: chipChannel(chip), query, agentNumber: agentFilter }, jumpPending);
  const sections = useMemo(
    () => buildListSections({ items: list.items, overview, chip, query, agentNumber: agentFilter, now }),
    [list.items, overview, chip, query, agentFilter, now],
  );

  // Search text → URL (replace: typing is not history).
  useEffect(() => {
    updateParams((p) => withListFilters(p, parseChip(p), query), { replace: true });
  }, [query, updateParams]);

  const onChipChange = (next: ListChip) => {
    updateParams((p) => withListFilters(p, next, query), { replace: true });
  };

  // --- selection ------------------------------------------------------------------
  // Bumped by every pick; a jump resolving later must not override it.
  const pickSeqRef = useRef(0);
  const select = useCallback(
    (next: Selection, opts: { focusComposer?: boolean; replace?: boolean } = {}) => {
      pickSeqRef.current += 1;
      const state: ListNavState = { fromList: !opts.replace };
      updateParams((p) => withSelection(p, next, { focusComposer: opts.focusComposer }), {
        replace: !!opts.replace,
        state,
      });
    },
    [updateParams],
  );

  const onSelectRow = (row: ListRow) => {
    const next = selectionForRow(row);
    if (sameSelection(next, selection)) return;
    // A customer waiting for a person: open ready to write.
    const focusComposer = row.kind === 'waiting' && row.waiting.state !== 'callback';
    select(next, { focusComposer });
  };

  /** „Erledigt“ of a Rückruf-Ticket; rejects so the row keeps the note for another try. */
  const resolveTicket = useCallback(
    async (ticket: CallbackTicket, note: string) => {
      try {
        const done = await callbackTicketService.resolve(ticket.id, note);
        applyCallbackTicket(done);
        toast.success(`Rückruf von ${displayName(ticket.customer_name, ticket.customer_phone)} als erledigt markiert.`);
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          toast.error('Dieses Ticket gibt es nicht mehr – die Liste wird aktualisiert.');
          refreshOverview();
        } else {
          toast.error('Konnte nicht als erledigt markiert werden. Bitte versuche es erneut.');
        }
        throw error;
      }
    },
    [applyCallbackTicket, refreshOverview],
  );

  const closeChat = useCallback(() => {
    if ((location.state as ListNavState | null)?.fromList) {
      navigate(-1);
    } else {
      updateParams((p) => withSelection(p, null), { replace: true });
    }
  }, [location.state, navigate, updateParams]);

  // --- jumps (?phone=&at= from the dashboard / transfer banner) ---------------------
  const handledJumpRef = useRef<string | null>(null);
  const overviewRef = useRef(overview);
  overviewRef.current = overview;
  useEffect(() => {
    const jump = parseJump(searchParams);
    if (!jump) {
      handledJumpRef.current = null;
      return;
    }
    const key = `${jump.phone}|${jump.at ?? ''}|${jump.chip}`;
    if (handledJumpRef.current === key) return;
    handledJumpRef.current = key;
    const pickAtStart = pickSeqRef.current;
    setSearch(jump.phone);
    setJumpQuery(jump.phone);
    updateParams((p) => withListFilters(withoutJump(p), jump.chip, jump.phone), { replace: true });

    // A running call of this number (transfer banner): open it live.
    if (jump.chip !== 'whatsapp') {
      const call = findLiveCall(overviewRef.current, jump.phone);
      if (call) {
        select({ kind: 'call', sid: call.call_sid }, { replace: true });
        return;
      }
    }
    (async () => {
      try {
        const res = await conversationService.list(
          chipChannel(jump.chip),
          jump.phone,
          50,
          0,
          agentFilter || undefined,
          'contact',
        );
        // The user picked something while the jump was loading (theirs
        // wins), or left the page (no navigation back into it).
        if (pickSeqRef.current !== pickAtStart || !mountedRef.current) return;
        const best = pickBestMatch(res.items || [], jump.phone, jump.at);
        if (best) select(selectionForItem(best), { replace: true });
      } catch {
        // The list itself shows the error; the jump just does not select.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reacts to URL changes only
  }, [searchParams]);

  // --- workspace chrome ---------------------------------------------------------------
  const [wideContextOpen, setWideContextOpen] = useState(readContextOpen);
  const [sheetOpen, setSheetOpen] = useState(false);
  const layout: WorkspaceChrome['layout'] = !isDesktop ? 'mobile' : isWide ? 'wide' : 'medium';
  const contextOpen = layout === 'wide' ? wideContextOpen : sheetOpen;
  const onContextOpenChange = useCallback(
    (open: boolean) => {
      if (layout === 'wide') {
        setWideContextOpen(open);
        writeContextOpen(open);
      } else {
        setSheetOpen(open);
      }
    },
    [layout],
  );
  const selectionKey = !selection
    ? ''
    : selection.kind === 'contact'
      ? `contact:${selection.key}`
      : selection.kind === 'call'
        ? `call:${selection.sid}`
        : `conversation:${selection.id}`;
  // Another chat opened: the drawer belongs to the previous one.
  useEffect(() => {
    setSheetOpen(false);
  }, [selectionKey]);

  // An old `?c=<id>` WhatsApp link resolves to its contact; scroll there once.
  const [sessionJump, setSessionJump] = useState<{ key: string; conversationId: number } | null>(null);
  const onResolveContact = useCallback(
    (contactKey: string, conversationId: number) => {
      setSessionJump({ key: contactKey, conversationId });
      updateParams((p) => {
        const next = withSelection(p, { kind: 'contact', key: contactKey });
        if (p.get('focus') === 'composer') next.set('focus', 'composer');
        return next;
      }, { replace: true });
    },
    [updateParams],
  );

  const onInitialSessionHandled = useCallback(() => setSessionJump(null), []);

  // A live call ended: its stored transcript takes over (preloaded when possible).
  const detailCacheRef = useRef(new Map<number, ConversationDetailResponse>());
  const onCallEnded = useCallback(
    (conversationId: number, preloaded: ConversationDetailResponse | null) => {
      if (preloaded) detailCacheRef.current.set(conversationId, preloaded);
      updateParams((p) => withSelection(p, { kind: 'conversation', id: conversationId }), { replace: true });
    },
    [updateParams],
  );

  const onComposerFocused = useCallback(() => {
    updateParams(withoutComposerFocus, { replace: true });
  }, [updateParams]);

  const chrome: WorkspaceChrome = {
    layout,
    contextOpen,
    onContextOpenChange,
    onBack: layout === 'mobile' ? closeChat : undefined,
  };

  const knownContactKey =
    selection?.kind === 'conversation'
      ? list.items.find((i) => i.id === selection.id && i.type === 'whatsapp' && i.contact_key)?.contact_key ?? null
      : null;

  const workspace = !selection ? null : selection.kind === 'contact' ? (
    <ContactWorkspace
      key={selectionKey}
      contactKey={selection.key}
      me={me}
      agentLabel={agentLabel}
      focusComposer={wantsComposerFocus(searchParams)}
      onComposerFocused={onComposerFocused}
      initialSession={sessionJump?.key === selection.key ? sessionJump.conversationId : null}
      onInitialSessionHandled={onInitialSessionHandled}
      {...chrome}
    />
  ) : selection.kind === 'call' ? (
    <CallWorkspace
      key={selectionKey}
      callSid={selection.sid}
      me={me}
      agentLabel={agentLabel}
      onEnded={onCallEnded}
      {...chrome}
    />
  ) : !list.loadedOnce ? (
    // The list may already know this session's contact: wait for it instead
    // of loading the session only to be redirected to the contact chat.
    <ChatSkeleton onBack={chrome.onBack} />
  ) : (
    <SessionWorkspace
      key={selectionKey}
      conversationId={selection.id}
      me={me}
      agentLabel={agentLabel}
      initialDetail={detailCacheRef.current.get(selection.id) ?? null}
      knownContactKey={knownContactKey}
      onResolveContact={onResolveContact}
      {...chrome}
    />
  );

  const waiting = waitingCount(overview, agentFilter);

  const listPane = (
    <ConversationListPane
      sections={sections}
      chip={chip}
      onChipChange={onChipChange}
      waitingCount={waiting}
      search={search}
      onSearchChange={setSearch}
      loading={list.loading}
      error={list.error}
      onRetry={list.reload}
      selection={selection}
      onSelect={onSelectRow}
      agentLabel={agentLabel}
      showAgent={agents.length > 1}
      query={query}
      agents={agents}
      agentFilter={agentFilter}
      onAgentFilterChange={setAgentFilter}
      onResolveTicket={resolveTicket}
    />
  );

  return (
    <Layout fullWidth>
      {/* No page heading (Tom, 30.09.: every line above the columns costs chat
          space). The title lives in the sidebar, the tab title and this sr-only
          h1. Negative margins take back most of Layout's padding so the
          columns start at the top and use the full height. */}
      <div className="-mb-2 -mt-3 flex min-h-0 flex-1 flex-col md:-mx-4 md:-my-5">
        <h1 className="sr-only">Verläufe</h1>
        {/* One console surface with hairline columns. Only the columns scroll;
            min-h-0 / min-w-0 keep flex children from blowing out Layout's <main>. */}
        <div className="flex min-h-0 flex-1 overflow-hidden rounded-2xl border border-border bg-card shadow-[0_1px_2px_hsl(var(--foreground)/0.04),0_16px_40px_-20px_hsl(var(--foreground)/0.18)]">
          <aside
            aria-label="Verläufe"
            className="flex min-h-0 w-full flex-col md:w-[296px] md:flex-shrink-0 md:border-r md:border-border lg:w-[312px] 2xl:w-[336px]"
          >
            {listPane}
          </aside>
          {isDesktop &&
            (workspace ?? (
              <div className="hidden min-w-0 flex-1 flex-col items-center justify-center gap-2 bg-chrome/50 px-8 text-center md:flex">
                <MessagesSquare className="h-12 w-12 text-muted-foreground/30" aria-hidden="true" />
                <p className="text-sm font-medium text-foreground">Wähle links einen Chat oder Anruf</p>
                <p className="max-w-xs text-xs text-muted-foreground">
                  Wer auf das Team wartet und welche Anrufe gerade laufen, steht dort ganz oben.
                </p>
              </div>
            ))}
        </div>
      </div>

      {!isDesktop && workspace && <MobileChatDialog onClose={closeChat}>{workspace}</MobileChatDialog>}
    </Layout>
  );
}
